import { FeatureSettingName, featureSettingsObservable, getFeatureSetting } from "common/feature-settings";
import { shallowEqualArrays } from "common/Support";
import * as React from "react";

export function useFeatureSettings(names: readonly FeatureSettingName[]): boolean[] {
    const [result, setResult] = React.useState(names.map(name => getFeatureSetting(name)));
    const resultRef = React.useRef(result);
    const firstRun = React.useRef(false);
    resultRef.current = result;
    React.useEffect(() => {
        if (!firstRun) {
            setResult(names.map(name => getFeatureSetting(name)));
        }
        firstRun.current = false;

        const cb = () => {
            const newValue = names.map(name => getFeatureSetting(name));
            if (!shallowEqualArrays(resultRef.current, newValue)) {
                setResult(newValue);
            }
        };
        featureSettingsObservable.subscribe(cb);
        return () => featureSettingsObservable.unsubscribe(cb);
        // Unpack the names so changing the array wont cause us to re-subscribe for no reason
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...names]);

    return result;
}

type WithFeatureSettingsProps = Partial<Record<FeatureSettingName, boolean>>;

export const withFeatureSetting = <T extends FeatureSettingName, P extends Record<T, boolean>>(
    Component: React.ComponentType<P>,
    features: readonly T[]
): React.FunctionComponent<Omit<P, keyof Pick<P, T>>> => p => {
    const f: WithFeatureSettingsProps = {};
    const r = useFeatureSettings(features);
    features.forEach((feature, index) => (f[feature] = r[index]));
    return <Component {...(p as P)} {...f} />;
};
